// Inbox.js

import React, { useEffect, useMemo, useRef, useState } from 'react'
import * as signalR from '@microsoft/signalr'
import authService from '../components/api-authorization/AuthorizeService'

import Container from '@mui/material/Container'
import Card from '@mui/material/Card'
import CardHeader from '@mui/material/CardHeader'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Avatar from '@mui/material/Avatar'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import ListItemText from '@mui/material/ListItemText'
import Divider from '@mui/material/Divider'
import CircularProgress from '@mui/material/CircularProgress'
import TextField from '@mui/material/TextField'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import SendIcon from '@mui/icons-material/Send'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import Switch from '@mui/material/Switch'
import FormControlLabel from '@mui/material/FormControlLabel'
import Stack from '@mui/material/Stack'
import Select from '@mui/material/Select'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'

// ---------- helpers ----------

const safeDate = msg => {
    const d = new Date(msg.receivedAt || msg.createdAt || 0)
    return Number.isNaN(d.getTime()) ? null : d
}

const formatDateTime = d => (d ? d.toLocaleString() : '')

const getInitials = (name, fallback) => {
    const src = (name || fallback || '?').trim()
    const parts = src.split(' ')
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
    return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase()
}

// deterministic-ish gradient per chat key
const gradients = [
    'linear-gradient(135deg,#6366F1,#EC4899)',
    'linear-gradient(135deg,#06B6D4,#3B82F6)',
    'linear-gradient(135deg,#F97316,#FACC15)',
    'linear-gradient(135deg,#10B981,#14B8A6)',
    'linear-gradient(135deg,#8B5CF6,#6366F1)'
]

const getGradientForKey = (key) => {
    let hash = 0
    const s = String(key || '')
    for (let i = 0; i < s.length; i++) {
        hash = ((hash << 5) - hash) + s.charCodeAt(i)
        hash |= 0
    }
    const idx = Math.abs(hash) % gradients.length
    return gradients[idx]
}

const SELECT_BG = 'rgba(34,197,94,0.15)'
const SELECT_BG_HOVER = 'rgba(34,197,94,0.20)'

// ---------- component ----------

const InboxPage = () => {
    const [rows, setRows] = useState([])
    const [loading, setLoading] = useState(true)
    const [selectedChatKey, setSelectedChatKey] = useState(null)

    const [menuAnchor, setMenuAnchor] = useState(null)
    const [menuMessage, setMenuMessage] = useState(null)

    const [rulesOpen, setRulesOpen] = useState(false)
    const [rules, setRules] = useState([])
    const [rulesLoading, setRulesLoading] = useState(false)
    const [rulesSaving, setRulesSaving] = useState(false)

    const connRef = useRef(null)

    // Group messages into chats by chatId / from
    const chats = useMemo(() => {
        const map = new Map()

        rows.forEach(msg => {
            const key = msg.chatId || msg.from || 'unknown'
            const d = safeDate(msg)

            if (!map.has(key)) {
                map.set(key, {
                    key,
                    from: msg.from,
                    fromName: msg.fromName,
                    messages: [],
                    lastDate: d,
                    lastText: msg.text
                })
            }

            const chat = map.get(key)
            chat.messages.push(msg)

            if (!chat.lastDate || (d && d > chat.lastDate)) {
                chat.lastDate = d
                chat.lastText = msg.text
                chat.from = msg.from
                chat.fromName = msg.fromName
            }
        })

        const arr = Array.from(map.values())

        arr.forEach(chat => {
            chat.messages.sort((a, b) => {
                const da = safeDate(a) || 0
                const db = safeDate(b) || 0
                return da - db
            })
        })

        arr.sort((a, b) => {
            const da = a.lastDate || 0
            const db = b.lastDate || 0
            return db - da
        })

        return arr
    }, [rows])

    const selectedChat =
        useMemo(
            () => chats.find(c => c.key === selectedChatKey) || null,
            [chats, selectedChatKey]
        )

    useEffect(() => {
        let cancelled = false;
            (async () => {
                try {
                    const res = await fetch('/api/inbox?take=200', { credentials: 'include' })
                    const data = await res.json()
                    if (!cancelled) {
                        const arr = Array.isArray(data) ? data : []
                        setRows(arr)
                    }
                } catch (e) {
                    console.error('Load inbox failed', e)
                } finally {
                    if (!cancelled) setLoading(false)
                }

                const token = await authService.getAccessToken()
                if (!token || cancelled) return

                const connection = new signalR.HubConnectionBuilder()
                    .withUrl('/alertHub', {
                        accessTokenFactory: () => token,
                        transport: signalR.HttpTransportType.LongPolling
                    })
                    .configureLogging(signalR.LogLevel.Debug)
                    .withAutomaticReconnect()
                    .build()

                connection.on('InboxNewMessage', (msg) => {
                    console.log('InboxNewMessage from hub:', msg)
                    setRows(prev => [msg, ...prev].slice(0, 500))
                    setSelectedChatKey(prevKey => prevKey ?? (msg.chatId || msg.from || 'unknown'))
                })

                try {
                    await connection.start()
                    connRef.current = connection
                    console.log('Inbox connected to AlertHub')
                } catch (err) {
                    console.error('Inbox hub start failed', err)
                }
            })()

        return () => {
            cancelled = true
            if (connRef.current) connRef.current.stop()
        }
    }, [])

    useEffect(() => {
        if (!selectedChatKey && chats.length > 0) {
            setSelectedChatKey(chats[0].key)
        }
    }, [chats, selectedChatKey])

    const handleMenuOpen = (event, msg) => {
        event.stopPropagation()
        setMenuAnchor(event.currentTarget)
        setMenuMessage(msg)
    }

    const handleMenuClose = () => {
        setMenuAnchor(null)
        setMenuMessage(null)
    }

    const loadRules = async () => {
        setRulesLoading(true)
        try {
            const res = await fetch('/api/inbox/rules', { credentials: 'include' })
            const data = await res.json()
            if (Array.isArray(data)) {
                setRules(data)
            } else {
                setRules([])
            }
        } catch (e) {
            console.error('Failed to load rules', e)
        } finally {
            setRulesLoading(false)
        }
    }

    const handleOpenRules = async () => {
        setRulesOpen(true)
        await loadRules()
    }

    const handleCloseRules = () => {
        if (rulesSaving) return
        setRulesOpen(false)
    }

    const handleRuleChange = (index, field, value) => {
        setRules(prev => {
            const copy = [...prev]
            copy[index] = { ...copy[index], [field]: value }
            return copy
        })
    }

    const handleAddRule = () => {
        setRules(prev => [
            ...prev,
            {
                id: 0,           // backend can treat 0 as "new"
                enabled: true,
                pattern: '',
                action: 'notify',
                priority: 10
            }
        ])
    }

    const handleDeleteRule = (index) => {
        setRules(prev => prev.filter((_, i) => i !== index))
    }

    const handleSaveRules = async () => {
        setRulesSaving(true)
        try {
            const res = await fetch('/api/inbox/rules', {
                method: 'PUT',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(rules)
            })
            if (!res.ok) {
                throw new Error('Save rules failed')
            }
            setRulesOpen(false)
        } catch (e) {
            console.error(e)
            // optionally show a toast/snackbar here
        } finally {
            setRulesSaving(false)
        }
    }

    return (
        <>
        <Container
            maxWidth={false}      
            disableGutters
            sx={{
                mt: 2,
                px: 3,
                pb: 2,
                height: 'calc(100vh - 64px)'
            }}
        >
            <Box>
                <Typography
                    variant="h4"
                    sx={{ fontWeight: 'bold', color: 'text.primary', mt: 2 }}
                >
                    Inbox
                </Typography>
                <Typography variant="body2" sx={{ mb: 3 }}>
                    Messages forwarded from n8n (WhatsApp / others).
                </Typography>
            </Box>
                <Card sx={{ minHeight: 560, width: '100%' }}>
                    <Box
                        sx={{
                            px: 2,
                            py: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}
                    >
                        <Box />
                        <IconButton
                            size="small"
                            onClick={handleOpenRules}
                            sx={{ ml: 0.5 }}
                            aria-label="Filter rules"
                        >
                            <SettingsOutlinedIcon fontSize="small" />
                        </IconButton>
                    </Box>
                <CardHeader
                    title="Connected Whatsapp"
                    //subheader="Messages forwarded from n8n (WhatsApp / others)."
                >
                    
                    </CardHeader>
                <CardContent sx={{ p: 0 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            height: 600,
                            borderTop: theme => `1px solid ${theme.palette.divider}`
                        }}
                    >
                        {/* LEFT: chat list */}
                        <Box
                            sx={{
                                width: 360,
                                borderRight: theme => `1px solid ${theme.palette.divider}`,
                                display: 'flex',
                                flexDirection: 'column'
                            }}
                        >
                            <Box sx={{ flex: 1, overflow: 'auto' }}>
                                {loading && (
                                    <Box
                                        sx={{
                                            height: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <CircularProgress size={24} />
                                    </Box>
                                )}

                                {!loading && chats.length === 0 && (
                                    <Box sx={{ p: 3 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            No messages yet. Send something through your n8n workflow ✉️
                                        </Typography>
                                    </Box>
                                )}

                                {!loading && chats.length > 0 && (
                                    <List disablePadding>
                                        {chats.map(chat => (
                                            <ListItemButton
                                                key={chat.key}
                                                selected={chat.key === selectedChatKey}
                                                onClick={() => setSelectedChatKey(chat.key)}
                                                alignItems="flex-start"
                                                sx={{
                                                    borderRadius: 2,
                                                    mx: 1,
                                                    my: 0.5,
                                                    '&.Mui-selected': {
                                                        bgcolor: SELECT_BG,
                                                        '&:hover': { bgcolor: SELECT_BG_HOVER }
                                                    }
                                                }}
                                            >
                                                <ListItemAvatar>
                                                    <Avatar
                                                        sx={{
                                                            width: 36,
                                                            height: 36,
                                                            color: 'common.white',
                                                            fontSize: 14,
                                                            fontWeight: 600,
                                                            backgroundImage: getGradientForKey(chat.key)
                                                        }}
                                                    >
                                                        {getInitials(chat.fromName, chat.from)}
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between'
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="subtitle2"
                                                                noWrap
                                                                sx={{ maxWidth: '60%', fontWeight: 700 }} // 👈 bold name
                                                            >
                                                                {chat.fromName || chat.from || 'Unknown'}
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{ fontWeight: 500 }}
                                                            >
                                                                {formatDateTime(chat.lastDate)}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                    secondary={
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            noWrap
                                                            sx={{ mt: 0.5 }}
                                                        >
                                                            {chat.lastText || ''}
                                                        </Typography>
                                                    }
                                                />
                                            </ListItemButton>
                                        ))}
                                    </List>
                                )}
                            </Box>
                        </Box>

                        {/* RIGHT: conversation panel */}
                        <Box
                            sx={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                bgcolor: 'background.default'
                            }}
                        >
                            {!selectedChat ? (
                                <Box
                                    sx={{
                                        flex: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        textAlign: 'center',
                                        color: 'text.secondary'
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 140,
                                            height: 140,
                                            borderRadius: '50%',
                                            border: theme => `2px dashed ${theme.palette.divider}`,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mb: 2
                                        }}
                                    >
                                        <Typography variant="h3" component="span">
                                            📩
                                        </Typography>
                                    </Box>
                                    <Typography variant="h6" gutterBottom>
                                        No conversation selected
                                    </Typography>
                                    <Typography variant="body2">
                                        Select a chat on the left to see the full thread here.
                                    </Typography>
                                </Box>
                            ) : (
                                <>
                                    {/* Chat header with light blue background */}
                                    <Box
                                        sx={{
                                            px: 4,
                                            pt: 3,
                                            pb: 2,
                                            borderBottom: theme =>
                                                `1px solid ${theme.palette.divider}`,
                                            bgcolor: SELECT_BG, 
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 0.5
                                        }}
                                    >
                                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                            {selectedChat.fromName || selectedChat.from || 'Unknown'}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ fontWeight: 500 }}
                                        >
                                            {selectedChat.from}
                                        </Typography>

                                        {selectedChat.messages.length > 1 && (
                                            <Chip
                                                label={`${selectedChat.messages.length - 1} previous messages`}
                                                size="small"
                                                variant="outlined"
                                                sx={{ alignSelf: 'flex-start', mt: 1 }}
                                            />
                                        )}
                                    </Box>

                                    {/* Conversation scroll area */}
                                        <Box
                                            sx={{
                                                flex: 1,
                                                overflow: 'auto',
                                                px: 4,
                                                py: 3,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'flex-start' 
                                            }}
                                        >
                                        {selectedChat.messages.map((msg, idx) => {
                                            const d = safeDate(msg)
                                            return (
                                                <Box
                                                    key={msg.seq ?? idx}
                                                    sx={{
                                                        mb: 2.5,
                                                        p: 2,
                                                        borderRadius: 1,
                                                        bgcolor: '#94ecb426',
                                                        boxShadow: '0 1px 3px rgba(15,23,42,0.08)',
                                                        position: 'relative',
                                                        maxWidth: '50%',          
                                                        display: 'inline-block',  
                                                        alignSelf: 'flex-start',  
                                                        wordBreak: 'break-word',  
                                                        '&:hover .msg-menu': {
                                                            opacity: 1,
                                                            pointerEvents: 'auto',
                                                            transform: 'translateY(0)'
                                                        },
                                                        '&::before': {
                                                            content: '""',
                                                            position: 'absolute',
                                                            left: -8,         
                                                            top: 2,          
                                                            width: 0,
                                                            height: 0,
                                                            borderStyle: 'solid',
                                                            borderWidth: '8px 8px 8px 0', 
                                                            borderColor: 'transparent #94ecb426 transparent transparent'
                                                        }
                                                    }}
                                                >
                                                    {/* 3-dot details menu */}
                                                    <IconButton
                                                        size="small"
                                                        className="msg-menu"
                                                        onClick={(e) => handleMenuOpen(e, msg)}
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 8,
                                                            right: 8,
                                                            opacity: 0,
                                                            pointerEvents: 'none',
                                                            transform: 'translateY(-4px)',
                                                            transition: 'all 0.15s ease-in-out'
                                                        }}
                                                    >
                                                        <MoreVertIcon fontSize="small" />
                                                    </IconButton>

                                                    <Typography
                                                        variant="body1"
                                                        sx={{ whiteSpace: 'pre-wrap', pr: 4 }}
                                                    >
                                                        {msg.text}
                                                    </Typography>

                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            justifyContent: 'flex-end',
                                                            mt: 1
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            sx={{ fontWeight: 500 }}
                                                        >
                                                            {formatDateTime(d)}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                            )
                                        })}
                                    </Box>

                                    {/* Reply box (placeholder) */}
                                    <Box
                                        sx={{
                                            borderTop: theme => `1px solid ${theme.palette.divider}`,
                                            px: 4,
                                            py: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5
                                        }}
                                    >
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder="Start writing your response… (coming soon)"
                                            disabled
                                        />
                                        <IconButton color="primary" disabled>
                                            <SendIcon />
                                        </IconButton>
                                    </Box>
                                </>
                            )}

                            {/* Read-only message details menu */}
                            <Menu
                                anchorEl={menuAnchor}
                                open={Boolean(menuAnchor)}
                                onClose={handleMenuClose}
                                PaperProps={{
                                    sx: {
                                        minWidth: 230,
                                        p: 1,
                                        borderRadius: 3,
                                        boxShadow: '0 10px 30px rgba(15,23,42,0.20)'
                                    }
                                }}
                            >
                                <MenuItem disabled sx={{ cursor: 'default', '&.Mui-disabled': { opacity: 1 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body2" sx={{ minWidth: 80 }} color="text.secondary">
                                            Importance
                                        </Typography>
                                        <Chip
                                            size="small"
                                            label={menuMessage?.important ? 'Important' : 'Normal'}
                                            color={menuMessage?.important ? 'error' : 'default'}
                                            variant={menuMessage?.important ? 'filled' : 'outlined'}
                                        />
                                    </Box>
                                </MenuItem>

                                <MenuItem disabled sx={{ cursor: 'default', '&.Mui-disabled': { opacity: 1 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body2" sx={{ minWidth: 80 }} color="text.secondary">
                                            Action
                                        </Typography>
                                        <Chip
                                            size="small"
                                            label={menuMessage?.action || 'None'}
                                            variant="outlined"
                                        />
                                    </Box>
                                </MenuItem>

                                <MenuItem disabled sx={{ cursor: 'default', '&.Mui-disabled': { opacity: 1 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body2" sx={{ minWidth: 80 }} color="text.secondary">
                                            Rule
                                        </Typography>
                                        <Chip
                                            size="small"
                                            label={menuMessage?.rulePattern || '—'}
                                            variant="outlined"
                                        />
                                    </Box>
                                </MenuItem>
                            </Menu>
                        </Box>
                    </Box>
                </CardContent>
            </Card>
        </Container>
        <Dialog
          open={rulesOpen}
          onClose={handleCloseRules}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Message filter rules</DialogTitle>
          <DialogContent dividers>
            {rulesLoading ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <CircularProgress size={22} />
              </Box>
            ) : (
              <Stack spacing={2} sx={{ mt: 1 }}>
                {rules.map((rule, index) => (
                  <Box
                    key={index}
                    sx={{
                      borderRadius: 2,
                      border: theme => `1px solid ${theme.palette.divider}`,
                      p: 1.5
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 1
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!rule.enabled}
                            onChange={e =>
                              handleRuleChange(index, 'enabled', e.target.checked)
                            }
                          />
                        }
                        label="Enabled"
                      />
                      <Button
                        color="error"
                        size="small"
                        onClick={() => handleDeleteRule(index)}
                      >
                        Remove
                      </Button>
                    </Box>
        
                    <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                      <TextField
                        label="Keyword / pattern"
                        fullWidth
                        size="small"
                        value={rule.pattern || ''}
                        onChange={e =>
                          handleRuleChange(index, 'pattern', e.target.value)
                        }
                      />
                      <TextField
                        label="Priority"
                        type="number"
                        size="small"
                        sx={{ width: 120 }}
                        value={rule.priority ?? 10}
                        onChange={e =>
                          handleRuleChange(index, 'priority', Number(e.target.value) || 0)
                        }
                      />
                    </Stack>
        
                    <Stack direction="row" spacing={2}>
                      <Select
                        size="small"
                        label="Action"
                        value={rule.action || 'notify'}
                        onChange={e =>
                          handleRuleChange(index, 'action', e.target.value)
                        }
                        sx={{ minWidth: 160 }}
                      >
                        <MenuItem value="notify">notify (show in Inbox)</MenuItem>
                        <MenuItem value="ignore">ignore</MenuItem>
                      </Select>
                    </Stack>
                  </Box>
                ))}
        
                {rules.length === 0 && !rulesLoading && (
                  <Typography variant="body2" color="text.secondary">
                    No rules yet. Add at least one keyword to start filtering.
                  </Typography>
                )}
        
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleAddRule}
                  sx={{ alignSelf: 'flex-start', mt: 1 }}
                >
                  Add rule
                </Button>
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseRules} disabled={rulesSaving}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveRules}
              variant="contained"
              disabled={rulesSaving}
            >
              {rulesSaving ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
        </>
    )
}

export default InboxPage
